/**
 * ADM-856 — reads the resources a group is still affected to (the mirror of
 * "who is in this group": "who uses this group").
 *
 * Lives in shared/ rather than in a feature because it has two consumers in two
 * features — the dynamic-group builder panel and the groups delete refusal — and
 * cross-feature imports are forbidden. Same precedent as useGroupsData.
 */

import { useQuery } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { GroupsService } from "@/features/groups/services/groups.service";
import type { AffectedResourceGroup, AffectedResourcesReport } from "@/shared/types/affectedResources.types";

/** Registered in cacheInvalidations.ts under the "groups" central resource. */
const AFFECTED_RESOURCES_QUERY_KEY = ["affected-resources"] as const;

const STALE_TIME_MS = 30_000;
const CONFLICT_STATUS = 409;
const GROUP_IN_USE_ERROR = "group_in_use";

export type AffectedResourcesResult = {
  /** Undefined until the report is in — never coerced to 0, so a panel can show "—". */
  totalCount: number | undefined
  groups: AffectedResourceGroup[]
  isLoading: boolean
  isError: boolean
};

/**
 * @param groupId - undefined while the group is still a draft (creation mode):
 *   no request is issued at all.
 */
export function useAffectedResources(groupId: string | undefined): AffectedResourcesResult {
  const query = useQuery({
    queryKey: [...AFFECTED_RESOURCES_QUERY_KEY, groupId],
    queryFn: () => {
      if (!groupId) return Promise.resolve<AffectedResourcesReport>({ total_count: 0, groups: [] });
      return GroupsService.getAffectedResources(groupId);
    },
    enabled: Boolean(groupId),
    staleTime: STALE_TIME_MS,
  });

  return {
    totalCount: query.data?.total_count,
    groups: query.data?.groups ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
  };
}

const isAffectedResourcesReport = (value: unknown): value is AffectedResourcesReport =>
  typeof value === "object"
  && value !== null
  && "total_count" in value
  && typeof value.total_count === "number"
  && "groups" in value
  && Array.isArray(value.groups);

/**
 * Extracts the report from the terminal 409 refusing to delete a group still in
 * use, or null for anything else — including the 409 `requires_confirmation`,
 * which is the confirmable flow handled by useResourceUsageConfirmation.
 */
export function getGroupInUseAffectedResources(error: unknown): AffectedResourcesReport | null {
  if (!isAxiosError(error) || error.response?.status !== CONFLICT_STATUS) return null;

  const body: unknown = error.response.data;
  if (typeof body !== "object" || body === null) return null;
  if (!("error" in body) || body.error !== GROUP_IN_USE_ERROR) return null;
  if (!("affected_resources" in body) || !isAffectedResourcesReport(body.affected_resources)) return null;

  return body.affected_resources;
}
